import React, { useState } from "react";
import {
  fetchQuery,
  graphql,
  useMutation,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";

import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";
import { overviewWidgetSizes } from "../../WidgetAdd/utils/widgetSizes/overviewWidgetSizes";
import { WAWidgetSizeToOverviewSize } from "../../WidgetAdd/utils/WAWidgetSizeToOverviewSize";
import {
  WATeamSchedulePreview,
  WATEAM_SCHEDULE_PREVIEW_QUERY,
} from "./WATeamSchedulePreview";
import { WATeamScheduleSettings } from "./WATeamScheduleSettings";
import { WATeamScheduleMutation } from "./__generated__/WATeamScheduleMutation.graphql";
import {
  WATeamSchedulePreviewQuery,
  WATeamSchedulePreviewQuery$variables,
} from "./__generated__/WATeamSchedulePreviewQuery.graphql";
import { useDebouncedCallback } from "use-debounce";
import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  WATeamScheduleContentType,
  WATeamScheduleContext,
} from "./WATeamScheduleContext";
import { WidgetScreen } from "../../common/WidgetScreen";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"WidgetAddTeamSchedule">;
  route: UserLoggedInNavigatorRouteProp<"WidgetAddTeamSchedule">;
};

export const WATeamSchedule = withWebModal(WATeamScheduleC, true);
function WATeamScheduleC(props: Props) {
  const [previewQuery, loadPreview] =
    useQueryLoader<WATeamSchedulePreviewQuery>(WATEAM_SCHEDULE_PREVIEW_QUERY);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [withoutRoleSelected, setWithoutRoleSelected] = useState(false);
  const [contentType, setContentType] =
    useState<WATeamScheduleContentType>("DAILY_WITH_SUMMARY");
  const environment = useRelayEnvironment();

  const loadPreviewQuery = useDebouncedCallback(
    async (
      teamId: string,
      contentType: WATeamScheduleContentType,
      roleIds: string[],
      withoutRoleSelected: boolean
    ) => {
      try {
        const variables: WATeamSchedulePreviewQuery$variables = {
          storecastId: props.route.params.storecastId,
          input: {
            size: "TWO_X_FOUR",
            teamIds: [teamId],
            type: contentType,
            roleIds,
            withoutRoleSelected,
          },
        };
        await fetchQuery(
          environment,
          WATEAM_SCHEDULE_PREVIEW_QUERY,
          variables
        ).toPromise();
        loadPreview(variables);
      } catch {}
    },
    300
  );

  const onSelectTeam = (newTeamId: string | null) => {
    setTeamId(newTeamId);
    setRoleIds([]);
    setWithoutRoleSelected(false);
    if (newTeamId) {
      loadPreviewQuery(newTeamId, contentType, [], false);
    }
  };

  const onSelectContentType = (contentType: WATeamScheduleContentType) => {
    setContentType(contentType);
    if (teamId) {
      loadPreviewQuery(teamId, contentType, roleIds, withoutRoleSelected);
    }
  };

  const onSetRoleIds = (newRoleIds: string[]) => {
    setRoleIds(newRoleIds);
    if (teamId) {
      loadPreviewQuery(teamId, contentType, newRoleIds, withoutRoleSelected);
    }
  };

  const onSetWithoutRoleSelected = (v: boolean) => {
    setWithoutRoleSelected(v);
    if (teamId) {
      loadPreviewQuery(teamId, contentType, roleIds, v);
    }
  };

  const [createWidgetTeamScheduleMutation, loading] =
    useMutation<WATeamScheduleMutation>(graphql`
      mutation WATeamScheduleMutation(
        $storecastId: ID!
        $input: CreateWidgetTeamScheduleInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetTeamSchedule(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = (size: WidgetSizeT) => {
    if (teamId) {
      createWidgetTeamScheduleMutation({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: WAWidgetSizeToOverviewSize(size),
            type: contentType,
            teamIds: [teamId],
            roleIds,
            withoutRoleSelected,
          },
        },
        onCompleted: () => {
          props.navigation.pop(2);
        },
      });
    }
  };

  return (
    <WATeamScheduleContext.Provider
      value={{
        teamId,
        setTeamId: onSelectTeam,
        roleIds,
        setRoleIds: onSetRoleIds,
        withoutRoleSelected,
        setWithoutRoleSelected: onSetWithoutRoleSelected,
        contentType,
        setContentType: onSelectContentType,
      }}
    >
      <WidgetScreen
        settings={
          <WATeamScheduleSettings onClose={() => props.navigation.goBack()} />
        }
        preview={
          <WASizePreview
            loading={loading}
            onCreate={onCreate}
            unitSize={props.route.params.unitSize}
            maxSize={props.route.params.maxSize}
            widgetSizes={overviewWidgetSizes}
            disabled={teamId === null}
          >
            {(size) =>
              previewQuery ? (
                <WATeamSchedulePreview
                  query={previewQuery}
                  size={
                    {
                      1: "TWO_X_ONE" as const,
                      2: "TWO_X_TWO" as const,
                      3: "TWO_X_THREE" as const,
                      4: "TWO_X_FOUR" as const,
                    }[size.h] ?? "TWO_X_ONE"
                  }
                />
              ) : undefined
            }
          </WASizePreview>
        }
      />
    </WATeamScheduleContext.Provider>
  );
}
